/**
 * Role Based Access rules:
 * 1. Static permissions are those permissions which don't need any data apart from the user role.
 * 2. Dynamic permissions are permissions which need additional data to determine access.
 *
 * Live Document:
 * https://www.notion.so/xfers/AuthZ-in-Multi-tenant-system-9fa7f5583da9485eb4b4d384a12f917f
 *
 * Note:
 * Initially feature matrix was going to be generated by express server and sent to FE but decided to hard code for MVP.
 * Hence handling permission on FE might be deprecated in the future.
 */

import { PERMISSIONS, USER_ROLES, FeatureMatrixSelectorsType } from '~/types'
import useFeatureGate from '~/hooks/useFeatureGate'
import { ConfigManager } from '~/managers/ConfigManager'

function check(factors: boolean[]) {
  return factors.every(v => v === true)
}

type RuleType = Partial<
  Record<
    USER_ROLES,
    {
      static: Partial<PERMISSIONS[]>
      dynamic: Partial<Record<PERMISSIONS, (args: FeatureMatrixSelectorsType) => void>>
    }
  >
>

const rules: RuleType = {
  [USER_ROLES.OWNER]: {
    static: [
      PERMISSIONS.TRANSACTION,
      PERMISSIONS.DEVELOPER_TOOLS,
      PERMISSIONS.TEAM,
      PERMISSIONS.SANDBOX,
      PERMISSIONS.MANAGE_ACCOUNT_BANK,
    ],
    dynamic: {
      [PERMISSIONS.DISBURSEMENTS]: ({
        isId,
        isSg,
        hasGW,
        isBizVerified,
        hasFundPoolForIdDisbursement,
      }: FeatureMatrixSelectorsType) => {
        const { isDisbursementsDisabled } = useFeatureGate()
        if (isDisbursementsDisabled) return false

        if (isId) {
          return check([hasGW, isBizVerified, hasFundPoolForIdDisbursement])
        } else if (isSg) {
          return check([hasGW, isBizVerified])
        }
        return false
      },
      [PERMISSIONS.DEPOSIT]: ({ isBizVerified }: FeatureMatrixSelectorsType) => isBizVerified,
      [PERMISSIONS.WITHDRAW]: ({
        isId,
        isBizVerified,
        hasFundPoolForIdWithdraw,
        isSg,
      }: FeatureMatrixSelectorsType) => {
        const { isWithdrawDisabled } = useFeatureGate()
        if (isWithdrawDisabled) return false

        if (isId) {
          return check([isBizVerified, hasFundPoolForIdWithdraw])
        } else if (isSg) {
          return check([isBizVerified])
        }
        return false
      },
      [PERMISSIONS.MERCHANT_USERS]: ({ isSandbox, isId }: FeatureMatrixSelectorsType) => {
        if (isSandbox) return false
        return isId
      },
      [PERMISSIONS.MANAGE_ACCOUNT_BLOCKCHAIN_ADDRESS]: ({ hasDGW }) => hasDGW,
      [PERMISSIONS.INVOICING]: ({
        isHighRiskMerchant,
        isSandbox,
        isBizVerified,
      }: FeatureMatrixSelectorsType) => {
        if (isSandbox) return false
        if (isHighRiskMerchant) return false

        return check([isBizVerified, ConfigManager.isRegionalSite()])
      },
      [PERMISSIONS.CREDIT_CARDS]: () => {
        const { isCreditCardsEnabled } = useFeatureGate()
        if (isCreditCardsEnabled) return true

        return false
      },
    },
  },
  [USER_ROLES.ADMIN]: {
    static: [
      PERMISSIONS.TRANSACTION,
      PERMISSIONS.DEVELOPER_TOOLS,
      PERMISSIONS.TEAM,
      PERMISSIONS.SANDBOX,
      PERMISSIONS.MANAGE_ACCOUNT_BANK,
    ],
    dynamic: {
      [PERMISSIONS.DISBURSEMENTS]: ({
        isId,
        hasGW,
        isBizVerified,
        hasFundPoolForIdDisbursement,
        isSg,
      }: FeatureMatrixSelectorsType) => {
        const { isDisbursementsDisabled } = useFeatureGate()
        if (isDisbursementsDisabled) return false

        if (isId) {
          return check([hasGW, isBizVerified, hasFundPoolForIdDisbursement])
        } else if (isSg) {
          return check([hasGW, isBizVerified])
        }
        return false
      },
      [PERMISSIONS.DEPOSIT]: ({ isId, hasGW, isBizVerified, isSg }: FeatureMatrixSelectorsType) => {
        if (isId) {
          return check([hasGW, isBizVerified])
        } else if (isSg) {
          return check([isBizVerified])
        }
        return false
      },
      [PERMISSIONS.WITHDRAW]: ({
        isId,
        isBizVerified,
        hasFundPoolForIdWithdraw,
        isSg,
      }: FeatureMatrixSelectorsType) => {
        const { isWithdrawDisabled } = useFeatureGate()
        if (isWithdrawDisabled) return false

        if (isId) {
          return check([isBizVerified, hasFundPoolForIdWithdraw])
        } else if (isSg) {
          return check([isBizVerified])
        }
        return false
      },
      [PERMISSIONS.MERCHANT_USERS]: ({ isSandbox, isId }: FeatureMatrixSelectorsType) => {
        if (isSandbox) return false
        return isId
      },
      [PERMISSIONS.MANAGE_ACCOUNT_BLOCKCHAIN_ADDRESS]: ({ hasDGW }) => hasDGW,
      [PERMISSIONS.INVOICING]: ({
        isHighRiskMerchant,
        isSandbox,
        isBizVerified,
      }: FeatureMatrixSelectorsType) => {
        if (isSandbox) return false
        if (isHighRiskMerchant) return false

        return check([isBizVerified, ConfigManager.isRegionalSite()])
      },
      [PERMISSIONS.CREDIT_CARDS]: () => {
        const { isCreditCardsEnabled } = useFeatureGate()
        if (isCreditCardsEnabled) return true

        return false
      },
    },
  },
  [USER_ROLES.OPERATIONS]: {
    static: [PERMISSIONS.TRANSACTION, PERMISSIONS.SANDBOX],
    dynamic: {
      [PERMISSIONS.DEPOSIT]: ({
        isId,
        hasGW,
        isBizVerified,
        isSg,
        hasDGW,
      }: FeatureMatrixSelectorsType) => {
        if (isId) {
          return check([hasGW, isBizVerified])
        } else if (isSg) {
          return check([hasDGW, isBizVerified])
        }
        return false
      },
      [PERMISSIONS.WITHDRAW]: () => {
        return ConfigManager.isRegionalSite()
      },
      [PERMISSIONS.DISBURSEMENTS]: () => {
        return ConfigManager.isRegionalSite()
      },
      [PERMISSIONS.INVOICING]: ({
        isHighRiskMerchant,
        isSandbox,
        isBizVerified,
      }: FeatureMatrixSelectorsType) => {
        if (isSandbox) return false
        if (isHighRiskMerchant) return false

        return check([isBizVerified, ConfigManager.isRegionalSite()])
      },
    },
  },
  [USER_ROLES.DEVELOPER]: {
    static: [PERMISSIONS.TRANSACTION, PERMISSIONS.DEVELOPER_TOOLS, PERMISSIONS.SANDBOX],
    dynamic: {},
  },
  [USER_ROLES.VIEWER]: {
    static: [PERMISSIONS.TRANSACTION, PERMISSIONS.SANDBOX],
    dynamic: {
      [PERMISSIONS.MERCHANT_USERS]: ({ isSandbox, isId }: FeatureMatrixSelectorsType) => {
        if (isSandbox) return false
        return isId
      },
    },
  },
  [USER_ROLES.FINANCE]: {
    static: [PERMISSIONS.TRANSACTION],
    dynamic: {
      [PERMISSIONS.DISBURSEMENTS]: ({
        isId,
        hasGW,
        isBizVerified,
        hasFundPoolForIdDisbursement,
      }: FeatureMatrixSelectorsType) => {
        const { isDisbursementsDisabled } = useFeatureGate()
        if (isDisbursementsDisabled) return false

        if (isId) {
          return check([isBizVerified, hasGW, hasFundPoolForIdDisbursement])
        }
        return false
      },
      [PERMISSIONS.DEPOSIT]: ({ isId, hasGW, isBizVerified }: FeatureMatrixSelectorsType) => {
        if (isId) {
          return check([isBizVerified, hasGW])
        }
        return false
      },
      [PERMISSIONS.WITHDRAW]: ({
        isId,
        hasGW,
        hasFundPoolForIdWithdraw,
        isBizVerified,
      }: FeatureMatrixSelectorsType) => {
        const { isWithdrawDisabled } = useFeatureGate()
        if (isWithdrawDisabled) return false

        if (isId) {
          return check([isBizVerified, hasGW, hasFundPoolForIdWithdraw])
        }
        return false
      },
    },
  },
}

export default rules
